// White-label Admin Product Import - Split View
// Left: Alibaba Search | Right: SALESSPACE Preview & Edit
import { useState } from 'react';
import { Search, Package, Plus, RefreshCw, Eye, Edit3, Trash2, ImageIcon, Save, ArrowRight, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to strip Alibaba branding from text
const stripAlibabaBranding = (text) => {
  if (!text) return '';
  return text
    .replace(/alibaba/gi, '')
    .replace(/aliexpress/gi, '')
    .replace(/1688/gi, '')
    .replace(/taobao/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Generate SALESSPACE Pro product name
const generateSalesspaceName = (originalTitle) => {
  const cleanTitle = stripAlibabaBranding(originalTitle);
  // Capitalize first letter of each word and limit length
  const formatted = cleanTitle
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
    .slice(0, 60);
  return `SALESSPACE Pro ${formatted}`;
};

const AlibabaImportPage = () => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    ship_to_country: 'NG',
    supplier_country: 'CN',
    sort_type: 'sales_desc',
    min_price: '',
    max_price: '',
    markup: 2.5
  });

  // Selected product for editing (right panel)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editedProduct, setEditedProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Search Alibaba products
  const searchProducts = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const params = { 
        keywords: searchQuery, 
        page_size: 20,
        ship_to_country: searchFilters.ship_to_country,
        supplier_country: searchFilters.supplier_country,
        sort_type: searchFilters.sort_type,
        markup: searchFilters.markup
      };
      if (searchFilters.min_price) params.min_price = parseFloat(searchFilters.min_price);
      if (searchFilters.max_price) params.max_price = parseFloat(searchFilters.max_price);
      
      const response = await axios.get(`${API_URL}/api/alibaba/search`, { params });
      setSearchResults(response.data.products || []);
      toast.success(`Found ${response.data.total} products`);
    } catch (error) {
      toast.error('Failed to search products');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Select product for editing
  const selectForPublishing = (product) => {
    setSelectedProduct(product);
    // Pre-populate edited product with white-labeled data
    setEditedProduct({
      name: generateSalesspaceName(product.title),
      description: stripAlibabaBrandingFromDescription(product.description || product.title),
      price: product.customer?.total_raw || product.price * searchFilters.markup,
      category: product.category_name || 'General',
      images: product.images || [product.main_image],
      selectedImage: product.main_image || product.images?.[0],
      featured: false,
      // Hidden supplier data (stored but never exposed to customers)
      supplierProductId: product.product_id,
      supplierCostUSD: product.supplier?.cost_usd || product.price,
      supplierCountry: searchFilters.supplier_country,
      markup: searchFilters.markup
    });
  };

  // Strip Alibaba branding from description
  const stripAlibabaBrandingFromDescription = (desc) => {
    if (!desc) return 'Premium quality product available exclusively at SALESSPACE.';
    let clean = stripAlibabaBranding(desc);
    // Add SALESSPACE branding if description is too short
    if (clean.length < 50) {
      clean = `${clean} - Premium quality, fast shipping, exclusively at SALESSPACE.`;
    }
    return clean;
  };

  // Publish product to SALESSPACE
  const publishProduct = async () => {
    if (!editedProduct) return;

    setSaving(true);
    try {
      // Create product with hidden supplier fields
      const productData = {
        name: editedProduct.name,
        description: editedProduct.description,
        price: editedProduct.price,
        product_type: 'physical',
        category: editedProduct.category,
        image_url: editedProduct.selectedImage,
        featured: editedProduct.featured,
        stock: 100,
        tags: ['salesspace-pro', 'dropship'],
        // Alibaba data stored internally (hidden from customers)
        alibaba_data: {
          product_id: editedProduct.supplierProductId,
          supplier_cost_usd: editedProduct.supplierCostUSD,
          supplier_country: editedProduct.supplierCountry,
          markup: editedProduct.markup,
          original_title: selectedProduct.title,
          last_synced: new Date().toISOString()
        }
      };

      const adminToken = localStorage.getItem('salesspace_admin_token');
      await axios.post(`${API_URL}/api/products`, productData, { headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} });
      
      toast.success('Product published to SALESSPACE!');
      setSelectedProduct(null);
      setEditedProduct(null);
    } catch (error) {
      toast.error('Failed to publish product');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedProduct(null);
    setEditedProduct(null);
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 px-6 py-4">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Product Import Studio</h1>
            <p className="text-slate-400 text-sm mt-1">
              Search suppliers, customize products, publish to SALESSPACE
            </p>
          </div>
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
            White-label Mode
          </Badge>
        </div>
      </div>

      {/* Split View Container */}
      <div className="max-w-[1800px] mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[calc(100vh-140px)]">
          
          {/* LEFT PANEL - Alibaba Search */}
          <Card className="bg-slate-900 border-slate-800 flex flex-col overflow-hidden">
            <CardHeader className="border-b border-slate-800 pb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <Search className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Supplier Search</CardTitle>
                  <CardDescription className="text-slate-500 text-xs">Find products from global suppliers</CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="flex-1 overflow-hidden flex flex-col pt-4">
              {/* Search Bar */}
              <div className="flex gap-2 mb-4">
                <Input
                  data-testid="alibaba-search-input"
                  placeholder="Search products... (e.g., wireless earbuds)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchProducts()}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
                <Button 
                  onClick={searchProducts} 
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="search-button"
                >
                  {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>

              {/* Filters Row */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <select
                  value={searchFilters.supplier_country}
                  onChange={(e) => setSearchFilters({...searchFilters, supplier_country: e.target.value})}
                  className="bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                >
                  <option value="CN">China</option>
                  <option value="US">USA</option>
                  <option value="IN">India</option>
                </select>
                <select
                  value={searchFilters.ship_to_country}
                  onChange={(e) => setSearchFilters({...searchFilters, ship_to_country: e.target.value})}
                  className="bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                >
                  <option value="NG">Nigeria</option>
                  <option value="US">USA</option>
                  <option value="GB">UK</option>
                  <option value="ES">Spain</option>
                  <option value="DE">Germany</option>
                </select>
                <select
                  value={searchFilters.markup}
                  onChange={(e) => setSearchFilters({...searchFilters, markup: parseFloat(e.target.value)})}
                  className="bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                >
                  <option value="2.0">2x Markup</option>
                  <option value="2.5">2.5x Markup</option>
                  <option value="3.0">3x Markup</option>
                </select>
                <select
                  value={searchFilters.sort_type}
                  onChange={(e) => setSearchFilters({...searchFilters, sort_type: e.target.value})}
                  className="bg-slate-800 border border-slate-700 rounded-md p-2 text-sm text-white"
                >
                  <option value="sales_desc">Best Sellers</option>
                  <option value="price_asc">Price: Low</option>
                  <option value="price_desc">Price: High</option>
                </select>
              </div>

              {/* Search Results */}
              <ScrollArea className="flex-1">
                {searchResults.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                    <p className="text-slate-500">Search for products to import</p>
                  </div>
                ) : (
                  <div className="space-y-3 pr-4">
                    {searchResults.map((product) => (
                      <div
                        key={product.product_id}
                        data-testid={`product-card-${product.product_id}`}
                        className={`flex gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedProduct?.product_id === product.product_id
                            ? 'bg-emerald-500/10 border-emerald-500/50'
                            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                        }`}
                        onClick={() => selectForPublishing(product)}
                      >
                        <img
                          src={product.main_image || product.images?.[0] || 'https://via.placeholder.com/80'}
                          alt=""
                          className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium line-clamp-2">{product.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-orange-400 text-xs">
                              Supplier: {product.supplier?.cost || `$${product.price}`}
                            </span>
                            <ArrowRight className="w-3 h-3 text-slate-600" />
                            <span className="text-emerald-400 text-xs font-medium">
                              {product.customer?.total || `$${(product.price * searchFilters.markup).toFixed(2)}`}
                            </span>
                          </div>
                          {product.profit && (
                            <Badge className="mt-1 bg-blue-500/20 text-blue-400 text-xs border-0">
                              Profit: ${product.profit.amount_usd} ({product.profit.margin_percent}%)
                            </Badge>
                          )}
                        </div>
                        {selectedProduct?.product_id === product.product_id && (
                          <Check className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* RIGHT PANEL - SALESSPACE Preview & Editor */}
          <Card className="bg-slate-900 border-slate-800 flex flex-col overflow-hidden">
            <CardHeader className="border-b border-slate-800 pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div>
                    <CardTitle className="text-white text-lg">SALESSPACE Preview</CardTitle>
                    <CardDescription className="text-slate-500 text-xs">Customize before publishing</CardDescription>
                  </div>
                </div>
                {editedProduct && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="flex-1 overflow-auto pt-4">
              {!editedProduct ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-full bg-slate-800 flex items-center justify-center mb-4">
                    <Edit3 className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="text-white font-medium mb-2">Select a Product</h3>
                  <p className="text-slate-500 text-sm max-w-xs">
                    Click on a product from the search results to customize and publish to your store
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-full">
                  <div className="space-y-6 pr-4">
                    {/* Product Image */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-2">
                        PRODUCT IMAGE
                      </label>
                      <div className="relative aspect-square max-w-xs rounded-lg overflow-hidden bg-slate-800">
                        <img
                          src={editedProduct.selectedImage}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        <Badge className="absolute top-2 left-2 bg-emerald-500 text-white border-0">
                          SALESSPACE Pro
                        </Badge>
                      </div>
                      {/* Image Selector */}
                      {editedProduct.images?.length > 1 && (
                        <div className="flex gap-2 mt-3 flex-wrap">
                          {editedProduct.images.slice(0, 5).map((img, idx) => (
                            <button
                              key={idx}
                              onClick={() => setEditedProduct({...editedProduct, selectedImage: img})}
                              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                                editedProduct.selectedImage === img 
                                  ? 'border-emerald-500' 
                                  : 'border-slate-700 hover:border-slate-500'
                              }`}
                            >
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <Separator className="bg-slate-800" />

                    {/* Product Name */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-2">
                        PRODUCT NAME
                      </label>
                      <Input
                        data-testid="product-name-input"
                        value={editedProduct.name}
                        onChange={(e) => setEditedProduct({...editedProduct, name: e.target.value})}
                        className="bg-slate-800 border-slate-700 text-white"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        All supplier branding removed. Using &quot;SALESSPACE Pro&quot; prefix.
                      </p>
                    </div>

                    {/* Description */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-2">
                        DESCRIPTION
                      </label>
                      <Textarea
                        data-testid="product-description-input"
                        value={editedProduct.description}
                        onChange={(e) => setEditedProduct({...editedProduct, description: e.target.value})}
                        rows={4}
                        className="bg-slate-800 border-slate-700 text-white resize-none"
                      />
                    </div>

                    {/* Category */}
                    <div>
                      <label className="text-xs font-medium text-slate-400 block mb-2">
                        CATEGORY
                      </label>
                      <select
                        value={editedProduct.category}
                        onChange={(e) => setEditedProduct({...editedProduct, category: e.target.value})}
                        className="w-full bg-slate-800 border border-slate-700 rounded-md p-2 text-white"
                        data-testid="product-category-select"
                      >
                        <option value="Electronics">Electronics</option>
                        <option value="Fashion">Fashion</option>
                        <option value="Home">Home & Garden</option>
                        <option value="Sports">Sports & Outdoors</option>
                        <option value="Beauty">Beauty & Health</option>
                        <option value="General">General</option>
                      </select>
                    </div>

                    {/* Price Preview */}
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                      <label className="text-xs font-medium text-slate-400 block mb-3">
                        PRICING SUMMARY
                      </label>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Your Cost:</span>
                          <span className="text-orange-400 font-mono">
                            ${editedProduct.supplierCostUSD?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Markup:</span>
                          <span className="text-white">{editedProduct.markup}x</span>
                        </div>
                        <Separator className="bg-slate-700 my-2" />
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Customer Pays:</span>
                          <span className="text-emerald-400 font-bold text-lg">
                            ${editedProduct.price?.toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Your Profit:</span>
                          <span className="text-blue-400 font-medium">
                            ${(editedProduct.price - editedProduct.supplierCostUSD)?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Featured Toggle */}
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="text-sm font-medium text-white">Featured Product</label>
                        <p className="text-xs text-slate-500">Show on homepage</p>
                      </div>
                      <button
                        onClick={() => setEditedProduct({...editedProduct, featured: !editedProduct.featured})}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          editedProduct.featured ? 'bg-emerald-500' : 'bg-slate-700'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                          editedProduct.featured ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* Hidden Data Notice */}
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <p className="text-amber-400 text-xs">
                        <strong>White-label Mode:</strong> Supplier ID and cost data will be stored internally but never shown to customers.
                      </p>
                    </div>
                  </div>
                </ScrollArea>
              )}
            </CardContent>

            {/* Publish Button */}
            {editedProduct && (
              <div className="border-t border-slate-800 p-4">
                <Button
                  onClick={publishProduct}
                  disabled={saving}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6"
                  data-testid="publish-button"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Publish to SALESSPACE
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AlibabaImportPage;
